'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import useControllableState from 'helpers/hooks/useControllableState';
import Typography from '../typography';

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'key'> {
  label?: string;
  labelDesc?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: 'primary' | 'secondary';
  labelPosition?: 'top' | 'inline';
  inputRef?: React.RefObject<HTMLInputElement | null>;
  error?: string;
  errorMessage?: string;
  isValid?: boolean;
  hideCheckIcon?: boolean;
  validation?: (valueToValidate: string) => boolean;
  renderEndIcon?: () => React.ReactNode;
}

const Input: React.FC<InputProps> = ({
  label,
  labelDesc,
  onChange,
  onBlur,
  onFocus,
  variant = 'primary',
  labelPosition = 'top',
  className = '',
  inputRef,
  value: valueProp,
  errorMessage,
  validation,
  children,
  hideCheckIcon,
  isValid: isValidProp,
  defaultValue = '',
  ...props
}) => {
  const [value, setValue] = useControllableState(valueProp, defaultValue);
  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isErrored, setIsErrored] = useState(false);
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (validation) {
      const shouldBeValidated = isTouched && isEdited;
      const validated = validation?.(value as string);
      setIsValid(shouldBeValidated && !!validated);
      setIsErrored(shouldBeValidated && !validated);
    }
  }, [isEdited, isTouched, validation, value]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      setValue(event.target.value);
      setIsEdited(true);
    },
    [onChange, setValue],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setIsTouched(true);
      onBlur?.(e);
    },
    [onBlur],
  );

  const bgClassName = useMemo(
    () =>
      ({
        primary: 'bg-white border-neutral-500 focus:border-primary-green disabled:bg-neutral-400',
        secondary: 'bg-transparent border-white focus:border-white placeholder:text-white',
      })[variant],
    [variant],
  );

  const isInActiveState = useMemo(() => isFocused || !!value, [isFocused, value]);

  const labelClassName = classNames('leading-normal text-[16px]/[20px] font-medium', {
    'text-neutrals-black': variant === 'primary',
    'text-white': variant === 'secondary',
    'text-primary-green': isInActiveState,
    'absolute top-[6px] left-3 font-medium block transition duration-150 ease-out': labelPosition === 'inline',
  });

  const labelContainerClassName = classNames({
    'mb-2': labelPosition === 'top',
  });

  const inputClassName = classNames(
    'h-10 focus:ring-0 w-full border-0 border-b px-0 placeholder:text-[14px] placeholder:leading-normal focus:outline-none disabled:cursor-not-allowed',
    bgClassName,
    {
      'pt-5 pb-1': isInActiveState && label && labelPosition == 'inline',
      'border-red-500 focus:border-red-500':
        (!!props.error && isInActiveState) ||
        isErrored ||
        (typeof isValidProp !== 'undefined' && !isValidProp && isTouched),
      'border-green-500 focus:border-green-500': (!!isValidProp && isInActiveState) || isValid,
    },
    className,
  );

  return (
    <div className="relative">
      <div className={labelContainerClassName}>
        {label && (
          <Typography as="label" className={labelClassName}>
            {props.required ? `${label} *` : label}
          </Typography>
        )}
        {labelDesc && (
          <Typography as="label" className={labelClassName}>
            {` (${labelDesc})`}
          </Typography>
        )}
      </div>
      <div className="relative">
        <input
          className={inputClassName}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          ref={inputRef}
          value={value}
          {...props}
        />
        {(isValid || (isValidProp && !isInActiveState)) && !isFocused && !hideCheckIcon && (
          <CheckIcon
            data-testid="check-icon"
            className="absolute right-3 top-1/2 size-4 -translate-y-1/2 text-green-500"
          />
        )}
        {children}
      </div>
      {(isErrored || props.error) && (
        <Typography className="mt-3 text-[12px] font-medium leading-[16px] text-red-500" as="span">
          {props.error ?? errorMessage}
        </Typography>
      )}
    </div>
  );
};

export default Input;
